/**
 * Live-tennis V1c — match-level position context inside the trade panel.
 *
 * The walkthrough's ask: contracts + avg price on BOTH outcomes, paired
 * quantity, combined pair cost, and residual exposure visible on the market
 * surface, so the floating tracker isn't required mid-session.
 *
 * Numbers come from the same authoritative source the tracker uses
 * (accountStore.positions ← relay fills ledger, live-pushed) folded by the
 * pure pairSummary math — the panel does NOT invent its own position record.
 * Join is by the game row's own sibling market tickers, which works for
 * tennis where ticker→gameKey re-derivation does not.
 *
 * Labeling discipline (trader-review packet, Appendix A): everything here is
 * BLENDED — avg-based pair cost is an approximation when fills span prices —
 * so no figure is ever called "locked", in either fee state.
 *
 * Gross vs net follows the global Fees toggle (#512). Only the PAIRED edge
 * moves: Fees on subtracts that share's entry fee and relabels it; the
 * residual figures stay gross by contract and say so in their own tooltip.
 *
 * Fees come from what Kalshi actually billed (openFeesCents, pro-rated to the
 * paired share) — this is a POST-trade surface, where the fee is a fact rather
 * than a forecast, so it should reconcile against the trader's statement. The
 * pre-trade surfaces' maker-rate estimate (EditOrderPriceModal, orderCardModel)
 * is the FALLBACK only, for positions whose billed fees aren't reported yet,
 * and it is labelled "net est" on the face: a maker guess understates an entry
 * that crossed the spread by ~4×, which can flip the sign of the figure.
 *
 * The MM Rebate toggle is deliberately NOT folded in: order totals stay on the
 * gross maker fee (#452 / #467), and this is a total, not a per-contract
 * quote. Billed fees are pre-rebate too, so on the actual path the question
 * doesn't arise. Kalshi-only, like the ladder chips.
 */

import { useMemo } from 'react';
import { cn } from '@/lib/infra/utils';
import { useAccountStore } from '@/stores/accountStore';
import { useDisplaySettings } from '@/stores/displaySettings';
import { useFeeFor } from '@/services/hydraFees';
import { makerRateForEntry } from '@/lib/pricing/fees';
import { computeGamePairSummary, pairedEntryFeeCents } from '@/lib/trading/pairSummary';
import { fmtQty, fmtUsd, fmtUsdSigned } from '@/components/cards/cardFormat';
import type { GameRow } from './types';

/** Fractional-cents price — "17.75¢" / "79¢". Whole cents stay clean. */
function fmtAvgCents(cents: number | null): string {
  if (cents == null) return '—';
  const rounded = Math.round(cents * 100) / 100;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(2)}¢`;
}

export function PairContextCard({ game }: { game: GameRow }) {
  const positions = useAccountStore((s) => s.positions);
  const feeAdjusted = useDisplaySettings((s) => s.feeAdjustedOdds);
  const awayTicker = game.kalshi?.away?.marketTicker;
  const homeTicker = game.kalshi?.home?.marketTicker;
  // Entry-form hooks so the toggle and a late-arriving fee schedule both
  // recompute in place — no refresh, no position reload.
  const awayFeeEntry = useFeeFor(awayTicker);
  const homeFeeEntry = useFeeFor(homeTicker);

  const summary = useMemo(() => {
    if (!awayTicker || !homeTicker || awayTicker === homeTicker) return null;
    return computeGamePairSummary(positions, awayTicker, homeTicker);
  }, [positions, awayTicker, homeTicker]);

  // Computed in both fee states: cheap, and it keeps the toggle a pure
  // display switch rather than a branch that changes what gets calculated.
  const pairedFee = useMemo(
    () =>
      summary == null
        ? null
        : pairedEntryFeeCents({
            summary,
            awayFeeRate: makerRateForEntry(awayFeeEntry),
            homeFeeRate: makerRateForEntry(homeFeeEntry),
          }),
    [summary, awayFeeEntry, homeFeeEntry],
  );

  if (!summary) return null;

  // Four states the label must not lie about: gross (toggle off), net (toggle
  // on, real billed fees), net est (toggle on, no billed fees — a maker-rate
  // guess that says so on its face), and gross-with-a-reason (toggle on,
  // neither available) — where showing the raw figure beats synthesizing a
  // fee, the same call getTeamWinData makes on a null rate.
  //
  // The fee actually subtracted: 0 with the toggle off, null when it's on and
  // nothing can price it. Deriving figure and labels from this one value is
  // what keeps them from drifting apart.
  const appliedFee = feeAdjusted ? pairedFee : ({ cents: 0, basis: 'actual' } as const);
  // The paired figure is the only thing netting touches, so with nothing
  // paired there is no net on screen to advertise — every remaining figure
  // (cost basis, residual) is gross, and the chip has to say so.
  const showsPairedFigure = summary.blendedPairCostCents != null;
  const netApplied = feeAdjusted && appliedFee != null && showsPairedFigure;
  const estimated = netApplied && appliedFee.basis === 'estimate';
  const feesUnknown = appliedFee == null;
  const pairedEdgeCents = summary.grossPairedEdgeCents - (appliedFee?.cents ?? 0);
  const basisWord = netApplied ? (estimated ? 'net est' : 'net') : 'gross';
  const basisChip = feesUnknown ? 'blended · gross · fees unknown' : `blended · ${basisWord}`;
  const basisChipTitle = feesUnknown
    ? 'Blended = sum of weighted-average prices; an approximation when fills span prices. Fees are ON but nothing can price this pair — no billed entry fees on the position, and no usable maker rate (schedule still loading, an unrecognized series, or a flat-fee series) — so the paired figure stays GROSS rather than assume a fee.'
    : estimated
      ? 'Blended = sum of weighted-average prices; an approximation when fills span prices. Net est = the paired figure less an ESTIMATED entry fee at the maker rate — Kalshi has not reported the billed fees for this position, and an entry that crossed the spread paid more. Residual figures below remain gross.'
      : netApplied
        ? 'Blended = sum of weighted-average prices; an approximation when fills span prices. Net = the paired figure less the entry fees Kalshi actually billed on those contracts. Residual figures below remain gross.'
        : 'Blended = sum of weighted-average prices; an approximation when fills span prices. Gross = before fees/rebates — turn on Fees (bottom left) to net the paired figure.';

  const heavyCode =
    summary.heavySide === 'away' ? game.awayCode : summary.heavySide === 'home' ? game.homeCode : null;
  const heavyAvg =
    summary.heavySide === 'away' ? summary.away.avgCents : summary.home.avgCents;

  return (
    <div className="border-border border-b px-4 py-2" data-testid="pair-context-card">
      <div className="text-muted-foreground mb-1 flex flex-wrap items-baseline justify-between gap-x-2 text-[10px] font-semibold uppercase">
        <span title="Open Kalshi contracts on this match, from the same position source as the tracker. YES on a market and NO on its sibling both count toward that outcome.">
          Match position (K)
        </span>
        <span className="normal-case" title={basisChipTitle} data-testid="pair-context-basis">
          {basisChip}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 font-mono text-xs">
        <div className="min-w-0 break-words" title={`${game.awayCode}: cost ${fmtUsd(summary.away.costCents / 100)}`}>
          <div className="text-muted-foreground text-[10px]">{game.awayCode} · Qty @ Avg</div>
          {summary.away.qty > 0 ? `${fmtQty(summary.away.qty)} @ ${fmtAvgCents(summary.away.avgCents)}` : '—'}
        </div>
        <div className="min-w-0 break-words" title={`${game.homeCode}: cost ${fmtUsd(summary.home.costCents / 100)}`}>
          <div className="text-muted-foreground text-[10px]">{game.homeCode} · Qty @ Avg</div>
          {summary.home.qty > 0 ? `${fmtQty(summary.home.qty)} @ ${fmtAvgCents(summary.home.avgCents)}` : '—'}
        </div>
      </div>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 font-mono text-xs">
        <span
          data-testid="pair-context-paired"
          className={cn(
            // Keyed on the figure's SIGN, not on quantity: netting makes a
            // losing pair reachable at a blended cost near 100¢, and a loss
            // printed in green is the same lie #512 set out to remove.
            summary.pairedContracts <= 0
              ? 'text-muted-foreground'
              : pairedEdgeCents < 0
                ? 'text-negative'
                : 'text-positive',
          )}
          title={
            netApplied
              ? `Paired = min(qty A, qty B). Each pair settles at 100¢ whoever wins; edge shown is paired × (100¢ − blended pair cost) less ${fmtUsd((appliedFee?.cents ?? 0) / 100)} ${estimated ? 'ESTIMATED entry fee at the maker rate' : 'in billed entry fees'}. Settlement is free, so that is the whole fee if the pair is held to settlement — selling to close pays sell-side fees on top.`
              : feesUnknown
                ? 'Paired = min(qty A, qty B). Each pair settles at 100¢ whoever wins; edge shown is paired × (100¢ − blended pair cost), gross of fees — Fees is on, but this pair has no billed entry fees reported and no usable maker rate.'
                : 'Paired = min(qty A, qty B). Each pair settles at 100¢ whoever wins; edge shown is paired × (100¢ − blended pair cost), gross of fees.'
          }
        >
          Paired {fmtQty(summary.pairedContracts)}
          {summary.blendedPairCostCents != null && (
            <>
              {' '}@ {fmtAvgCents(summary.blendedPairCostCents)} → {fmtUsdSigned(pairedEdgeCents / 100)} {basisWord}
            </>
          )}
        </span>
        <span
          className={cn(summary.unpairedContracts > 0 ? 'text-amber-400' : 'text-muted-foreground')}
          title={
            summary.unpairedContracts > 0
              ? `Residual directional exposure: ${fmtQty(summary.unpairedContracts)} unmatched ${heavyCode} @ ${fmtAvgCents(heavyAvg)} — loses ${fmtUsd(summary.openRiskCents / 100)} basis if ${heavyCode} loses, adds ${fmtUsd(summary.openUpsideCents / 100)} gross if ${heavyCode} wins.`
              : 'No residual directional exposure — outcome quantities match.'
          }
        >
          {summary.unpairedContracts > 0
            ? `Unpaired ${fmtQty(summary.unpairedContracts)} ${heavyCode} (risk ${fmtUsd(summary.openRiskCents / 100)})`
            : 'Flat residual'}
        </span>
      </div>
    </div>
  );
}
